import { create } from 'zustand';

const MAX_SELECTED_SEATS = 50;

const useStadiumStore = create((set, get) => ({
  // ─── VIEW STATE ────────────────────────────────────
  viewMode: 'overview', // 'overview' | 'section'
  activeSection: null,  // section id when zoomed in
  hoveredSection: null, // section id on hover (overview)
  hoveredSeat: null,    // seat id on hover (section view)

  // ─── CAMERA ────────────────────────────────────────
  camera: { x: 0, y: 0, scale: 1 },

  // ─── SEATS ─────────────────────────────────────────
  seatMap: {},        // { [seatId]: seatObject }
  selectedSeats: [],  // array of seat ids

  // ─── CART ──────────────────────────────────────────
  cartOpen: false,

  // ─── ADMIN ─────────────────────────────────────────
  // When true, admins bypass the per-section "sold out" greying so they can
  // open fully-sold sections and inspect seat numbers, prices and buyers.
  adminBypassSoldOut: false,

  // ─── ACTIONS ───────────────────────────────────────
  setViewMode: (mode) => set({ viewMode: mode }),
  setActiveSection: (id) => set({ activeSection: id }),
  setHoveredSection: (id) => set({ hoveredSection: id }),
  setHoveredSeat: (id) => set({ hoveredSeat: id }),
  setCamera: (cam) => set({ camera: cam }),

  setSeatMap: (map) => set({ seatMap: map }),

  toggleSeatSelection: (seatId) => {
    const { selectedSeats, seatMap } = get();
    const seat = seatMap[seatId];
    if (!seat || seat.status === 'sold' || seat.status === 'held') return;

    if (selectedSeats.includes(seatId)) {
      set({
        selectedSeats: selectedSeats.filter(id => id !== seatId),
        seatMap: { ...seatMap, [seatId]: { ...seat, status: 'available' } },
      });
    } else {
      if (selectedSeats.length >= MAX_SELECTED_SEATS) return;
      set({
        selectedSeats: [...selectedSeats, seatId],
        seatMap: { ...seatMap, [seatId]: { ...seat, status: 'selected' } },
      });
    }
  },

  toggleBoxSelection: (seatIds) => {
    const { selectedSeats, seatMap } = get();
    // If ANY seat in the box is already selected, deselect all
    const anySelected = seatIds.some(id => selectedSeats.includes(id));
    if (anySelected) {
      const updated = { ...seatMap };
      seatIds.forEach(id => {
        if (updated[id]) updated[id] = { ...updated[id], status: 'available' };
      });
      set({
        selectedSeats: selectedSeats.filter(id => !seatIds.includes(id)),
        seatMap: updated,
      });
    } else {
      // Check if adding would exceed limit
      const available = seatIds.filter(id => {
        const s = seatMap[id];
        return s && s.status !== 'sold' && s.status !== 'held';
      });
      if (selectedSeats.length + available.length > MAX_SELECTED_SEATS) return;
      const updated = { ...seatMap };
      available.forEach(id => {
        updated[id] = { ...updated[id], status: 'selected' };
      });
      set({
        selectedSeats: [...selectedSeats, ...available],
        seatMap: updated,
      });
    }
  },

  clearSelection: () => {
    const { selectedSeats, seatMap } = get();
    const updated = { ...seatMap };
    selectedSeats.forEach(id => {
      if (updated[id]) updated[id] = { ...updated[id], status: 'available' };
    });
    set({ selectedSeats: [], seatMap: updated });
  },

  setCartOpen: (open) => set({ cartOpen: open }),

  setAdminBypassSoldOut: (value) => set({ adminBypassSoldOut: !!value }),

  zoomToSection: (sectionId) => {
    set({ viewMode: 'section', activeSection: sectionId });
  },

  zoomOut: () => {
    set({ viewMode: 'overview', activeSection: null, hoveredSeat: null });
  },

  getSelectedSeatDetails: () => {
    const { selectedSeats, seatMap } = get();
    return selectedSeats.map(id => seatMap[id]).filter(Boolean);
  },

  getTotalPrice: () => {
    const { selectedSeats, seatMap } = get();
    return selectedSeats.reduce((sum, id) => {
      const seat = seatMap[id];
      return sum + (seat ? seat.price : 0);
    }, 0);
  },
}));

export default useStadiumStore;
